import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ChangeFinancialProductToolbarComponent from './Toolbar';
import { setFinancialProductIdOnSimulation } from 'actions/actionCreators/offer/financials/offerFinancingStructureActions';
import { IconButton } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';

const FinancialProductListComponent = ({
  availableFinancialProducts,
  onFilterValueChanged,
  fullTextFilter,
  onResetFilters,
  documentId,
  siteId,
  onProductSelection,
  onProductSelected,
  loading,
  loaded,
  disabledStatus
}) => {
  const [t] = useTranslation();

  const dispatch = useDispatch();

  const onFinancialProductSet = (documentId, siteId, financialProductId) => {
    onProductSelection(financialProductId);
    dispatch(
      setFinancialProductIdOnSimulation(documentId, siteId, financialProductId, hasError => onProductSelected(financialProductId, hasError))
    );
  };

  const getDisabledItemStatus = useCallback(
    financialProductId =>
      !!availableFinancialProducts.find(
        fp => !fp.selecting && fp.id === financialProductId && availableFinancialProducts.some(item => item.selecting || item.selected)
      ),
    [availableFinancialProducts]
  );

  return (
    <Grid item xs={12}>
      <Grid container>
        <Grid xs={6}>
          <ChangeFinancialProductToolbarComponent
            onFilterValueChanged={onFilterValueChanged}
            fullTextFilter={fullTextFilter}
            onResetFilters={onResetFilters}
            disabledStatus={disabledStatus}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {!loading && <WrappedFakeText lines={5} />}
        {!loaded && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t('OfferEdit.FinancingStructure.FinancialProductChangeModal.Name')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('OfferEdit.FinancingStructure.FinancialProductChangeModal.ContractTypeName')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('OfferEdit.FinancingStructure.FinancialProductChangeModal.ValidTo')}</strong>
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableFinancialProducts.map(elem => {
                return (
                  <TableRow key={elem.id}>
                    <TableCell>{elem.name}</TableCell>
                    <TableCell>{elem.contractTypeName}</TableCell>
                    <TableCell>{elem.validTo}</TableCell>
                    <TableCell onClick={() => onFinancialProductSet(documentId, siteId, elem.id)} align='right'>
                      {elem.selecting ? (
                        <CircularProgress size={24} />
                      ) : elem.selected ? (
                        <CheckIcon color='success' />
                      ) : elem.hasError ? (
                        <ErrorIcon color='error' />
                      ) : (
                        <IconButton color='primary' variant='text' size='tiny' tooltip={'Select'} disabled={getDisabledItemStatus(elem.id)}>
                          <RadioButtonUncheckedIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Grid>
    </Grid>
  );
};

FinancialProductListComponent.propTypes = {
  availableFinancialProducts: PropTypes.array.isRequired,
  onFilterValueChanged: PropTypes.func.isRequired,
  fullTextFilter: PropTypes.string.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onProductSelection: PropTypes.func.isRequired,
  onProductSelected: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  disabledStatus: PropTypes.bool.isRequired
};

export default FinancialProductListComponent;
